/**
 * SosButton — Driver Emergency / SOS control (Phase 1, advisory).
 *
 * Additive safety UI. Does NOT touch any dispatch/ride flow. Press-and-hold
 * (1.5s) to arm, then choose a level — preventing accidental activation. While
 * an SOS is active it streams location pings to the backend (history only) and
 * shows a persistent ACTIVE state with a "Stand down" action.
 *
 * Disclaimer: operator alerting tool, NOT a 999/emergency-services dispatch.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { ShieldAlert, X, Phone, LifeBuoy, Loader2, CheckCircle2, MessageSquare } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const HOLD_MS = 1500;
const DISCLAIMER = "This is an operator alerting tool, not a 999/emergency-services dispatch system. In a life-threatening emergency, call 999.";

// Pre-loaded driver replies. During an emergency a driver may not be able to
// type freely, so replies are one-tap quick actions (admin still free-texts).
const QUICK_REPLIES = [
  "I am safe.", "I need help.", "Call me now.", "Cannot talk.", "Passenger issue.",
  "Vehicle issue.", "Medical issue.", "Police required.", "False alarm.", "Resolved.",
];

export const SosButton = ({ incomingSignal = null, triggerClassName = "fixed bottom-4 right-4 z-[55] w-16 h-16 rounded-full bg-[#DC2626] hover:bg-[#B91C1C] text-white shadow-2xl flex flex-col items-center justify-center select-none active:scale-95 transition-transform border-4 border-white/30" } = {}) => {
  const [active, setActive] = useState(null);     // active SOS event or null
  const [sheetOpen, setSheetOpen] = useState(false);
  const [armed, setArmed] = useState(false);      // press-and-hold completed
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const holdTimer = useRef(null);
  const pingTimer = useRef(null);
  const msgTimer = useRef(null);
  const lastCount = useRef(0);

  // Restore active state on mount (e.g. after a refresh).
  useEffect(() => {
    api.get("/driver/sos/active").then(({ data }) => setActive(data.active)).catch(() => {});
  }, []);

  const getPos = () => new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null), { enableHighAccuracy: true, timeout: 5000 });
  });

  // While SOS active → stream location pings every 8s (history only).
  useEffect(() => {
    if (!active) { if (pingTimer.current) clearInterval(pingTimer.current); return; }
    const ping = async () => {
      const pos = await getPos();
      if (pos) api.post("/driver/sos/ping", pos).catch(() => {});
    };
    ping();
    pingTimer.current = setInterval(ping, 8000);
    return () => { if (pingTimer.current) clearInterval(pingTimer.current); };
  }, [active]);

  // While SOS active → poll the Control Room conversation thread every 4s.
  const loadMessages = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/sos/messages");
      const msgs = data.messages || [];
      setMessages(msgs);
      const fromAdmin = msgs.filter((m) => m.from_role === "admin").length;
      if (!chatOpen && msgs.length > lastCount.current && fromAdmin > 0) {
        setUnread((u) => u + (msgs.length - lastCount.current));
      }
      lastCount.current = msgs.length;
    } catch { /* best-effort */ }
  }, [chatOpen]);

  useEffect(() => {
    if (!active) { setMessages([]); lastCount.current = 0; if (msgTimer.current) clearInterval(msgTimer.current); return; }
    loadMessages();
    // WS (Driver.jsx `sos_message`) drives instant refresh — same pattern as
    // ride chat. This interval is just a conservative safety-net backup.
    msgTimer.current = setInterval(loadMessages, 10000);
    return () => { if (msgTimer.current) clearInterval(msgTimer.current); };
  }, [active, loadMessages]);

  // WS-driven refresh: re-fetch the thread the moment the control room messages.
  useEffect(() => {
    if (incomingSignal && active) loadMessages();
  }, [incomingSignal, active, loadMessages]);

  const sendReply = useCallback(async (text) => {
    const body = (text || "").trim();
    if (!body || sending) return;
    setSending(body);
    try {
      const { data } = await api.post("/driver/sos/message", { message: body });
      setMessages((prev) => [...prev, data.message]);
      lastCount.current += 1;
    } catch (e) {
      toast.error(formatApiError(e) || "Could not send message");
    } finally { setSending(false); }
  }, [sending]);

  const raise = useCallback(async (level) => {
    setBusy(true);
    try {
      const pos = await getPos();
      const { data } = await api.post("/driver/sos", { level, ...(pos || {}) });
      setActive(data);
      setSheetOpen(false); setArmed(false);
      toast.error(level === "emergency" ? "SILENT EMERGENCY raised — control room alerted" : "Assistance requested — control room alerted");
    } catch (e) {
      toast.error(formatApiError(e) || "Could not raise SOS");
    } finally { setBusy(false); }
  }, []);

  const standDown = useCallback(async () => {
    setBusy(true);
    try {
      await api.post("/driver/sos/stand-down", { notes: "Stood down by driver" });
      setActive(null);
      toast.success("SOS stood down. Stay safe.");
    } catch (e) {
      toast.error(formatApiError(e) || "Could not stand down");
    } finally { setBusy(false); }
  }, []);

  const startHold = () => {
    holdTimer.current = setTimeout(() => { setArmed(true); setSheetOpen(true); }, HOLD_MS);
  };
  const cancelHold = () => { if (holdTimer.current) clearTimeout(holdTimer.current); };

  // ── Active banner ──
  if (active) {
    const emergency = active.level === "emergency";
    return (
      <div
        data-testid="sos-active-banner"
        className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[92%] max-w-md rounded-xl border-2 px-4 py-3 shadow-2xl ${emergency ? "bg-[#991B1B] border-[#7F1D1D]" : "bg-[#B45309] border-[#92400E]"} text-white`}
      >
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 animate-pulse" />
          <div className="flex-1">
            <div className="text-sm font-bold tracking-wide">{emergency ? "SILENT EMERGENCY ACTIVE" : "ASSISTANCE REQUESTED"}</div>
            <div className="text-[11px] opacity-90">Control room alerted · sharing your live location</div>
          </div>
        </div>
        {!emergency && (
          <button onClick={() => raise("emergency")} disabled={busy} data-testid="sos-escalate-btn"
            className="mt-2 w-full text-xs font-semibold bg-[#7F1D1D] hover:bg-[#641313] rounded-lg py-2">
            Escalate to Silent Emergency
          </button>
        )}
        <button onClick={() => { setChatOpen((o) => !o); setUnread(0); }} data-testid="sos-chat-toggle"
          className="mt-2 w-full text-xs font-semibold bg-white/15 hover:bg-white/25 rounded-lg py-2 flex items-center justify-center gap-1.5">
          <MessageSquare className="w-3.5 h-3.5" /> Control Room chat
          {messages.length > 0 && <span className="opacity-80">({messages.length})</span>}
          {unread > 0 && <span className="ml-1 bg-white text-[#991B1B] rounded-full px-1.5 text-[10px] font-bold" data-testid="sos-chat-unread">{unread}</span>}
        </button>

        {chatOpen && (
          <div className="mt-2 bg-white/10 rounded-lg p-2" data-testid="sos-chat-panel">
            <div className="max-h-40 overflow-y-auto space-y-1.5 mb-2" data-testid="sos-chat-thread">
              {messages.length === 0 ? (
                <div className="text-[11px] opacity-80 text-center py-3">No messages yet. The control room can see your alert.</div>
              ) : messages.map((m) => (
                <div key={m.id} className={`flex ${m.from_role === "driver" ? "justify-end" : "justify-start"}`} data-testid={`sos-chat-msg-${m.from_role}`}>
                  <div className={`max-w-[80%] rounded-lg px-2.5 py-1.5 text-[12px] ${m.from_role === "driver" ? "bg-white text-[#0A0A0A]" : "bg-black/30 text-white"}`}>
                    <div className="text-[9px] uppercase tracking-wide opacity-70 mb-0.5">{m.from_role === "driver" ? "You" : (m.from_name || "Control Room")}</div>
                    {m.body}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-white/15 pt-2">
              <div className="text-[10px] uppercase tracking-wide opacity-70 mb-1.5">Tap to reply</div>
              <div className="grid grid-cols-2 gap-1.5" data-testid="sos-quick-replies">
                {QUICK_REPLIES.map((q) => (
                  <button key={q} onClick={() => sendReply(q)} disabled={!!sending}
                    data-testid={`sos-quick-reply-${q.replace(/[^a-zA-Z]+/g, "-").toLowerCase().replace(/^-|-$/g, "")}`}
                    className="text-[12px] font-semibold bg-white/15 hover:bg-white/30 active:bg-white text-white active:text-[#991B1B] rounded-lg py-2 px-2 disabled:opacity-40 transition-colors flex items-center justify-center gap-1">
                    {sending === q ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}{q}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <button onClick={standDown} disabled={busy} data-testid="sos-stand-down-btn"
          className="mt-2 w-full text-xs font-semibold bg-white/15 hover:bg-white/25 rounded-lg py-2 flex items-center justify-center gap-1.5">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} I'm safe — stand down
        </button>
      </div>
    );
  }

  // ── Hold-to-arm button + level sheet ──
  return (
    <>
      <button
        data-testid="sos-hold-button"
        onMouseDown={startHold} onMouseUp={cancelHold} onMouseLeave={cancelHold}
        onTouchStart={startHold} onTouchEnd={cancelHold}
        className={triggerClassName}
        title="Press and hold for emergency / SOS"
      >
        <ShieldAlert className="w-6 h-6" />
        <span className="text-[8px] font-bold mt-0.5">SOS</span>
      </button>

      {sheetOpen && armed && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center p-3" data-testid="sos-level-sheet">
          <div className="bg-white w-full max-w-md rounded-2xl p-4 shadow-2xl">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-bold text-[#991B1B] flex items-center gap-1.5"><ShieldAlert className="w-5 h-5" /> Raise an emergency alert</h3>
              <button onClick={() => { setSheetOpen(false); setArmed(false); }} data-testid="sos-cancel-btn" className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Choose the level. Your name, vehicle, current trip and live location will be sent to the control room immediately.</p>
            <button onClick={() => raise("assistance")} disabled={busy} data-testid="sos-level-assistance"
              className="w-full text-left rounded-xl border-2 border-[#F59E0B] bg-[#FFFBEB] hover:bg-[#FEF3C7] p-3 mb-2 flex items-center gap-3">
              <LifeBuoy className="w-6 h-6 text-[#B45309]" />
              <div><div className="text-sm font-bold text-[#92400E]">Assistance Needed</div><div className="text-[11px] text-[#92400E]/80">I need help but I'm not in immediate danger.</div></div>
            </button>
            <button onClick={() => raise("emergency")} disabled={busy} data-testid="sos-level-emergency"
              className="w-full text-left rounded-xl border-2 border-[#DC2626] bg-[#FEF2F2] hover:bg-[#FEE2E2] p-3 flex items-center gap-3">
              <ShieldAlert className="w-6 h-6 text-[#991B1B]" />
              <div><div className="text-sm font-bold text-[#991B1B]">Silent Emergency</div><div className="text-[11px] text-[#991B1B]/80">I'm in danger — alert the control room now.</div></div>
            </button>
            {busy && <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending alert…</div>}
            <div className="mt-3 text-[10px] text-muted-foreground flex items-start gap-1.5 border-t border-border pt-2">
              <Phone className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {DISCLAIMER}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SosButton;
